import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { getEnrollmentSegment } from "EmployeeApplication/selectors";
import AgeEnrollmentModal, { AgeEnrollmentFormValueProps } from "EmployeeCoverage/Components/EnrollmentModal/AgeEnrollmentModal";
import SegmentEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/SegmentEnrollmentModal";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatNameFL } from "Utils/formatName";
import { EmployeeForm, EmployeePolicy, LongTermDisabilityPlan, PlanCost } from "Utils/types";

import Button, { ButtonProps } from "Cobalt/Button";

import { useLongTermDisabilityContext } from "./longTermDisability.context";

interface EntitiesToAgeFormInitialValuesProps {
  employeeForm: EmployeeForm;
  employeePolicy: EmployeePolicy;
}
const entitiesToAgeFormInitialValues = ({ employeeForm, employeePolicy }: EntitiesToAgeFormInitialValuesProps): AgeEnrollmentFormValueProps => {
  return {
    employeeEnrolling: employeePolicy.isEnrollingLongTermDisability,
    employeeFormattedName: formatNameFL(employeeForm),
    dependents: [],
  };
};

export const LongTermDisabilityEnrollmentModals = () => {
  const { plan, planCost, isShowing, hideEnrollmentModal } = useLongTermDisabilityContext();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const employeeDependents = useSelector(getDependents) || [];
  const dispatch = useDispatch();

  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hideEnrollmentModal();
  };

  const onSubmitAgeEnrollment = async (values) => {
    const isEnrollingLongTermDisability = values.employeeEnrolling;
    const body = {
      id: employeePolicy.id,
      isEnrollingLongTermDisability: values.employeeEnrolling,
      enrolledLongTermDisabilityPlanID: isEnrollingLongTermDisability === "Yes" ? plan.id : null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseLongTermDisabilityPlan"));
    }
  };

  if (planCost?.pricingType === "age") {
    return (
      <AgeEnrollmentModal
        plan={plan}
        employeePolicyID={employeePolicy.id}
        initialValues={entitiesToAgeFormInitialValues({
          employeeForm,
          employeePolicy,
        })}
        title="Enroll In Long Term Disability Coverage"
        onSubmit={onSubmitAgeEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const currentSegment = getEnrollmentSegment(employeePolicy, employeeDependents, "long_term_disability");

  const onSubmitSegmentEnrollment = async ({ selectedSegment }) => {
    const body = {
      id: employeePolicy.id,
      isEnrollingLongTermDisability: selectedSegment === "none" ? "No" : "Yes",
      enrolledLongTermDisabilityPlanID: selectedSegment === "none" ? null : plan.id,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseLongTermDisabilityPlan"));
    }
  };

  if (planCost?.pricingType === "segment") {
    return (
      <SegmentEnrollmentModal
        plan={plan}
        planCost={planCost}
        title="Enroll In Long Term Disability Coverage"
        initialValues={{ selectedSegment: currentSegment }}
        onSubmit={onSubmitSegmentEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  return null;
};

interface LongTermDisabilityEnrollOrDeclineButtonProps extends ButtonProps {
  plan: LongTermDisabilityPlan;
  planCost: PlanCost;
}

export const LongTermDisabilityEnrollOrDeclineButton = ({ plan, planCost, ...rest }: LongTermDisabilityEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useLongTermDisabilityContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({ plan, planCost });
  };

  return (
    <Button onClick={openModal} {...rest}>
      Enroll or Decline
    </Button>
  );
};
